import React, { useId } from 'react';
import NextImage from 'next/image';
import { type ImageField, type LinkField } from 'lib/types';
import { cn } from 'lib/utils';
import { Bloom, Button, Icon, Link, Wrapper } from 'ui';

export type GlobalFooterProps = {
  className?: string;
  ctaHeading: string;
  ctaImage: ImageField;
  ctaLink?: LinkField;
  sections: {
    title: string;
    /** `isExternal` marks a link as leaving Oura, showing an arrow beside it. */
    links: (LinkField & { isExternal?: boolean })[];
  }[];
  utilityLinks?: LinkField[];
};

const GlobalFooter = ({
  className,
  ctaHeading,
  ctaImage,
  ctaLink,
  sections,
  utilityLinks,
}: GlobalFooterProps) => {
  const id = useId();

  return (
    <footer
      className={cn(
        'relative z-2 overflow-hidden lg:px-5 lg:py-6 lg:bg-graphite-900',
        className
      )}
      aria-label="Site footer"
    >
      <Bloom
        className="lg:hidden"
        positionX="left"
        positionY="bottom"
        variant="athletic"
      />

      <Wrapper
        className="flex flex-col lg:flex-row lg:justify-center py-8 px-6 lg:p-0 bg-stone-950/10 lg:rounded-lg overflow-hidden"
        variant="header"
      >
        {/* CTA Sidebar */}
        <aside
          className="relative max-lg:h-80 lg:w-100 lg:shrink-0 px-6 py-8 lg:p-10"
          aria-labelledby={`${id}-cta`}
        >
          <div className="relative z-3 flex flex-col gap-10">
            <h4 id={`${id}-cta`}>{ctaHeading}</h4>

            {!!ctaLink?.text && (
              <div>
                <Button href={ctaLink.href} target={ctaLink.target}>
                  {ctaLink.text}
                </Button>
              </div>
            )}
          </div>

          {/* Background Image */}
          <div className="absolute inset-0 size-full max-lg:rounded-lg lg:rounded-l-lg overflow-hidden">
            <NextImage
              src={ctaImage.src}
              alt={ctaImage.alt}
              width={ctaImage.width}
              height={ctaImage.height}
              className="absolute inset-0 size-full object-cover object-center"
            />
          </div>

          {/* Color Gradient */}
          <div
            className="z-2 absolute top-0 left-0 h-full w-full max-lg:bg-0/50 lg:bg-gradient-to-b lg:from-black/50 lg:via-25% lg:via-black/50 lg:to-transparent lg:to-75% max-lg:rounded-lg lg:rounded-l-lg"
            aria-hidden="true"
          />
        </aside>

        {/* Main Content Area */}
        <section
          className="relative lg:grow flex flex-col gap-12 lg:gap-16 py-8 max-lg:pt-8 lg:pl-18 overflow-hidden"
          aria-label="Footer navigation"
        >
          <div className="relative z-2 lg:pr-10 grid grid-cols-1 2xs:grid-cols-2 xl:grid-cols-3 gap-10 lg:gap-y-15">
            {sections.map((section, index) => (
              <nav key={index} aria-labelledby={`${id}-nav-${index}`}>
                <h5
                  id={`${id}-nav-${index}`}
                  className="text-p2 font-bold text-neutral-300 mb-5"
                >
                  {section.title}
                </h5>

                <ul className="flex flex-col gap-5">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex} className="text-p2">
                      <Link
                        href={link.href}
                        target={link.target}
                        rel={link.rel}
                        className="no-underline"
                        trackingProps={link.trackingProps}
                        aria-label={
                          link.target === '_blank'
                            ? `${link.text} (opens in new window)`
                            : link.isExternal
                              ? `${link.text} (external link)`
                              : undefined
                        }
                      >
                        <span>{link.text}</span>
                        {link.isExternal && (
                          <Icon
                            name="external-link-arrow"
                            className="inline-block h-2.5 w-auto ml-1.5 align-middle"
                          />
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>

          <div className="flex flex-col gap-3 text-p3">
            <ul className="flex items-center gap-3 lg:gap-x-4 flex-wrap">
              {utilityLinks?.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    target={link.target}
                    variant="neutral-dark"
                  >
                    {link.text}
                  </Link>
                </li>
              ))}
            </ul>

            <p className="text-neutral-300">
              &copy; {new Date().getFullYear()} Oura Health Oy. All rights
              reserved. ŌURA, OURA, and Ō are trademarks of Oura Health Oy and
              may not be used without permission.
            </p>
          </div>

          <Bloom
            className="max-lg:hidden"
            positionX="left"
            positionY="bottom"
            variant="blue"
          />

          <Bloom
            className="max-lg:hidden"
            positionX="right"
            positionY="top"
            variant="blue"
          />
        </section>
      </Wrapper>
    </footer>
  );
};

export default GlobalFooter;
